import React from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import NuevaSolicitudButton from "./nuevaSolicitud";
import UploadDocuments from "./UploadDocuments";
import StateChange from "./StateChange";
import { apiPost } from "../api";

var solicitudesGeneral = [];

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function createData(solicitud) { return { solicitud }; }

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
};

// Estas dos propagan el error en vez de devolver [] ante cualquier fallo:
// devolver lista vacia hacia que un token vencido se viera igual que "no hay
// solicitudes", sin ningun aviso al usuario.
const getSolicitudes = () => apiPost(process.env.REACT_APP_API_GET_LIST, {});

const deleteBorrador = (id) => apiPost(process.env.REACT_APP_API_DELETE_BORRADOR, { id });

// Normaliza para comparaciones sin tildes ni mayúsculas
function normEst(s) {
  return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function estadoToBadge(estado) {
  const n = normEst(estado);
  if (n === 'borrador' || n === 'para validar') return { cls: 'draft', label: estado };
  if (n === 'esperando documentacion tecnica') return { cls: 'wait', label: estado };
  if (n === 'esperando documentacion legal') return { cls: 'wait', label: estado };
  if (n === 'esperando cao') return { cls: 'wait', label: estado };
  if (n === 'en evaluacion tecnica') return { cls: 'eval', label: estado };
  if (n.includes('evaluacion legal') || n.includes('certronic')) return { cls: 'eval', label: estado };
  if (n === 'habilitado') return { cls: 'ok', label: 'Habilitado' };
  if (n.includes('obra en ejecucion')) return { cls: 'exec', label: 'Obra en ejecución' };
  if (n.includes('obra finalizada') || n.includes('cao presentado')) return { cls: 'ok', label: estado };
  // Cierre del ciclo. En gris (no verde) porque se puede llegar desde
  // "En Seguimiento" sin haber recorrido el flujo: es un final, no un exito.
  if (n === 'cerrado') return { cls: 'closed', label: 'Cerrada' };
  // Desenlace lateral: la solicitud no interfiere, no recorre el flujo.
  if (n.includes('en seguimiento')) return { cls: 'eval', label: 'En seguimiento' };
  if (n.includes('desestimado')) return { cls: 'rejected', label: 'Desestimada' };
  return { cls: 'draft', label: estado };
}

// Mini-steps del timeline
// idx: 0=Presentada, 1=Doc.técnica, 2=Doc.legal, 3=Habilitada, 4=Obra, 5=CAO
// El orden sigue el flujo real de la API: el CAO se presenta despues de la
// obra (Obra finalizada → Esperando CAO → CAO presentado → Cerrado).
// Cada paso de documentacion muestra su sub-fase (En espera / En evaluación)
// en vez de ocupar dos pasos distintos del timeline.
const MINI_STEPS = ['Presentada', 'Doc. técnica', 'Doc. legal', 'Habilitada', 'Obra', 'CAO'];

// Campo de fecha que prueba que cada paso efectivamente ocurrio. La API los
// completa a medida que la solicitud avanza (outFields=* los devuelve todos).
// idx del paso -> campo, en el mismo orden que MINI_STEPS.
const STEP_FECHA = [
  'fecha_alta_solicitud', // 0 Presentada
  'fecha_doc_tecnica',    // 1 Doc. técnica
  'fecha_doc_legal',      // 2 Doc. legal
  'fecha_habilitacion',   // 3 Habilitada
  'fecha_inicio_obra',    // 4 Obra
  'fecha_cao',            // 5 CAO
];

// En que paso esta parada la solicitud segun su estado, y con que sub-fase.
// Los "Esperando documentacion X" no son una etapa aparte: son el mismo paso
// esperando que el solicitante presente la documentacion.
function getPasoActual(estado) {
  const n = normEst(estado);
  if (n === 'borrador') return null;
  if (n === 'para validar') return { curr: 0, sub: null };
  if (n === 'esperando documentacion tecnica') return { curr: 1, sub: 'En espera' };
  if (n === 'en evaluacion tecnica') return { curr: 1, sub: 'En evaluación' };
  if (n === 'esperando documentacion legal') return { curr: 2, sub: 'En espera' };
  if (n.includes('evaluacion legal') || n.includes('certronic')) return { curr: 2, sub: 'En evaluación' };
  if (n === 'habilitado') return { curr: 3, sub: null };
  if (n.includes('obra en ejecucion')) return { curr: 4, sub: 'En ejecución' };
  if (n.includes('obra finalizada')) return { curr: 4, sub: 'Finalizada' };
  if (n === 'esperando cao') return { curr: 5, sub: 'En espera' };
  if (n.includes('cao presentado')) return { curr: 5, sub: 'Presentado' };
  // Cerrado y En Seguimiento son finales: no hay un paso "en curso". Que se
  // haya recorrido cada paso lo dicen las fechas, no el estado — a Cerrado se
  // puede llegar desde En Seguimiento sin haber pasado por doc/habilitacion/obra.
  if (n === 'cerrado' || n.includes('en seguimiento')) return { curr: null, sub: null };
  if (n.includes('desestimado')) return null;
  // Estado no contemplado: no inventamos avance (antes caia en curr=0 y el
  // timeline decia "Presentada" contradiciendo el badge de la fila).
  return null;
}

// Devuelve { cls, sub } para un paso del timeline.
// Tres situaciones distintas, con estilos distintos:
//   done    — tiene su fecha cargada: efectivamente ocurrio.
//   curr    — es el paso en el que esta parada la solicitud ahora.
//   skipped — quedo atras sin fecha: no se hizo (gris, no vacio).
//   ''      — todavia no se llego a ese paso.
function getMiniStep(attrs, idx) {
  const paso = getPasoActual(attrs?.estado);
  if (!paso) return { cls: '', sub: null };

  const { curr, sub } = paso;
  // El paso en curso manda sobre la fecha: fecha_doc_tecnica se carga al
  // SUBIR la documentacion, no al aprobarla, asi que el paso ya tiene fecha
  // mientras el estado sigue siendo "En evaluacion tecnica". Sin esto el paso
  // se pintaba verde (cumplido) cuando en realidad todavia se esta evaluando.
  if (idx === curr) {
    return { cls: sub === 'En espera' ? 'curr wait' : 'curr', sub };
  }
  if (attrs?.[STEP_FECHA[idx]]) return { cls: 'done', sub: null };
  // curr === null: estado final (Cerrado / En Seguimiento), no hay paso en
  // curso, asi que todo paso sin fecha quedo atras sin cumplirse.
  if (curr === null || idx < curr) return { cls: 'skipped', sub: null };
  return { cls: '', sub: null };
}

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/>
  </svg>
);
const ForwardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6"/>
  </svg>
);
const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <path d="M17 8l-5-5-5 5M12 3v12"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14"/>
  </svg>
);
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/>
    <path d="M12 9v4M12 17h.01"/>
  </svg>
);
const ChevRIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6"/>
  </svg>
);
const ChevLIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 6l-6 6 6 6"/>
  </svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
  </svg>
);

function ActionTail({ row, setUpload, setRow, setOpenEdit, setEditRow, setChangeState, loading, setNuevaSolicitudes, setDeletedText, setOpenDeleted }) {
  const estado = row.solicitud.attributes.estado;
  const n = normEst(estado);

  const deleteRow = () => {
    if (window.confirm("¿Seguro que querés eliminar la solicitud en borrador?")) {
      loading(true);
      deleteBorrador(row.solicitud.attributes.id).then(x => {
        if (x) { setDeletedText(x.message); setOpenDeleted(true); setNuevaSolicitudes(Math.random()); }
      }).catch(err => {
        // Sin este catch el loading quedaba prendido para siempre si fallaba.
        loading(false);
        if (err?.sesionVencida) return;
        setDeletedText(err?.message || 'No se pudo eliminar el borrador.');
        setOpenDeleted(true);
      });
    }
  };

  if (n === 'borrador') return (
    <div className="sw-tail-actions">
      <button className="ds-btn ds-btn-sm ds-btn-soft" onClick={() => { setOpenEdit(true); setEditRow(row); }}>
        <EditIcon /> Editar
      </button>
      <button className="ds-btn ds-btn-sm ds-btn-danger" onClick={deleteRow}>
        <TrashIcon /> Eliminar
      </button>
    </div>
  );

  if (n === 'habilitado' || n.includes('obra en ejecucion')) return (
    <div className="sw-tail-col">
      <span className="sw-status attn"><AlertIcon />Acción requerida</span>
      <button className="ds-btn ds-btn-sm ds-btn-primary" onClick={() => { setRow(row); setChangeState(true); }}>
        <ForwardIcon /> {n === 'habilitado' ? 'Iniciar obra' : 'Finalizar obra'}
      </button>
    </div>
  );

  const isActionRequired = requiereAccion(estado);
  const needsDoc = isActionRequired || n === 'en evaluacion tecnica' || n.includes('evaluacion legal') || n.includes('certronic');

  if (needsDoc) {
    const label = n === 'esperando documentacion tecnica' ? 'Subir doc. técnica'
      : n === 'esperando documentacion legal' ? 'Subir doc. legal'
      : n === 'esperando cao' ? 'Subir CAO'
      : 'Ver documentación';
    return (
      <div className="sw-tail-col">
        {isActionRequired
          ? <span className="sw-status attn"><AlertIcon />Acción requerida</span>
          : <span className="sw-status">Sin acciones pendientes</span>
        }
        <button
          className={`ds-btn ds-btn-sm ${isActionRequired ? 'ds-btn-primary' : 'ds-btn-ghost'}`}
          onClick={() => { setRow(row); setUpload(true); }}
        >
          <UploadIcon /> {label}
        </button>
      </div>
    );
  }

  return (
    <div className="sw-tail-col">
      <span className="sw-status">Sin acciones pendientes</span>
    </div>
  );
}

const KPI_CONFIG = [
  { key: 'accion', label: 'Acción requerida', color: 'var(--st-wait)' },
  { key: 'evaluacion', label: 'En evaluación', color: 'var(--st-eval)' },
  { key: 'obra', label: 'En obra', color: 'var(--st-exec)' },
  { key: 'finalizadas', label: 'Finalizadas', color: 'var(--st-ok)' },
];

const FILTER_OPTIONS = [
  { key: 'all',      label: 'Todos' },
  { key: 'draft',    label: 'Borradores' },
  { key: 'accion',   label: 'Acción requerida' },
  { key: 'eval',     label: 'En evaluación' },
  { key: 'obra',     label: 'En obra' },
  { key: 'fin',      label: 'Finalizadas' },
];

// Unica definicion de "requiere accion del solicitante". La usan tanto el
// filtro/KPI como la fila: antes estaban duplicadas y no coincidian — "Obra en
// ejecucion" mostraba "Accion requerida" en la fila pero no contaba en el KPI,
// asi que al filtrar por accion esas filas desaparecian.
export function requiereAccion(estado) {
  const n = normEst(estado);
  return n === 'esperando documentacion tecnica'
    || n === 'esperando documentacion legal'
    || n === 'esperando cao'
    || n === 'habilitado'          // tiene que iniciar la obra
    || n.includes('obra en ejecucion'); // tiene que finalizarla
}

function matchesFilter(estado, key) {
  if (key === 'all') return true;
  const n = normEst(estado);
  if (key === 'draft')  return n === 'borrador' || n === 'para validar';
  if (key === 'accion') return requiereAccion(estado);
  if (key === 'eval')   return n === 'en evaluacion tecnica' || n.includes('evaluacion legal') || n.includes('certronic');
  if (key === 'obra')   return n.includes('obra en ejecucion');
  // Incluye los estados finales que faltaban: "cerrado" y "en seguimiento".
  if (key === 'fin')    return n.includes('obra finalizada') || n.includes('cao presentado')
    || n.includes('desestimado') || n === 'cerrado' || n.includes('en seguimiento');
  return true;
}

export default function SolicitudList(props) {
  const [clickRow, setRow] = React.useState(null);
  const [editRow, setEditRow] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [solicitudes, setSolicitudes] = React.useState(false);
  const [nuevaSolicitudes, setNuevaSolicitudes] = React.useState(false);
  const [rowsPerPage] = React.useState(10);
  const [upload, setUpload] = React.useState(false);
  const [changeState, setChangeState] = React.useState(false);
  const [openDeleted, setOpenDeleted] = React.useState(false);
  const [deletedText, setDeletedText] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [stateFilter, setStateFilter] = React.useState('all');
  const [bannerItems, setBannerItems] = React.useState({ habilitadas: [], docuTecnica: [], docuLegal: [], docuCAO: [] });
  const [loadError, setLoadError] = React.useState('');

  React.useEffect(() => {
    solicitudesGeneral = [];
    const habilitadas = [], docuTecnica = [], docuLegal = [], docuCAO = [];
    props.setLoading(true);
    setLoadError('');
    getSolicitudes().then(list => {
      if (!list.solicitudes) { props.setLoading(false); return; }
      list.solicitudes.forEach(linea => {
        solicitudesGeneral.push(createData(linea));
        const n = normEst(linea.attributes.estado);
        if (n === 'habilitado') habilitadas.push(linea.attributes.id);
        if (n === 'esperando documentacion tecnica') docuTecnica.push(linea.attributes.id);
        if (n === 'esperando documentacion legal') docuLegal.push(linea.attributes.id);
        if (n === 'esperando cao') docuCAO.push(linea.attributes.id);
      });
      solicitudesGeneral.sort((a, b) => b.solicitud.attributes.id - a.solicitud.attributes.id);
      setBannerItems({ habilitadas, docuTecnica, docuLegal, docuCAO });
      setSolicitudes(Math.random());
      props.setLoading(false);
    }).catch(err => {
      props.setLoading(false);
      // Si la sesion vencio, api.js ya redirige al login: no tiene sentido
      // mostrar un error que el usuario no llega a leer.
      if (err?.sesionVencida) return;
      setLoadError(err?.message || 'No se pudieron cargar las solicitudes.');
    });
  }, [nuevaSolicitudes]);

  const filtered = solicitudesGeneral.filter(row => {
    const a = row.solicitud.attributes;
    if (!matchesFilter(a.estado, stateFilter)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(a.id).includes(q) ||
      (a.denominacion || '').toLowerCase().includes(q) ||
      (a.estado || '').toLowerCase().includes(q) ||
      (a.detalle_instalacion || '').toLowerCase().includes(q) ||
      (a.tipo_cruce || '').toLowerCase().includes(q)
    );
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  // Los KPI se cuentan con matchesFilter, la misma regla que usa el filtro de
  // la tabla. Antes tenian su propia copia de las condiciones y quedaban
  // desincronizadas: "Finalizadas" no contaba "Cerrado", asi que una solicitud
  // cerrada no aparecia en el indicador aunque estuviera en la lista.
  const contarPorFiltro = (key) =>
    solicitudesGeneral.filter(r => matchesFilter(r.solicitud.attributes.estado, key)).length;

  const kpiValues = {
    accion: contarPorFiltro('accion'),
    evaluacion: contarPorFiltro('eval'),
    obra: contarPorFiltro('obra'),
    finalizadas: contarPorFiltro('fin'),
  };

  const bannerTotal = bannerItems.habilitadas.length + bannerItems.docuTecnica.length + bannerItems.docuLegal.length + bannerItems.docuCAO.length;

  return (
    <>
      {/* Modals */}
      <NuevaSolicitudButton
        setNuevaSolicitudes={setNuevaSolicitudes} setLoading={props.setLoading}
        setOpenEdit={setOpenEdit} openEdit={openEdit}
        setEditRow={setEditRow} editRow={editRow}
      />
      <UploadDocuments upload={upload} setUpload={setUpload} clickRow={clickRow} setNuevaSolicitudes={setNuevaSolicitudes} />
      <StateChange changeState={changeState} setChangeState={setChangeState} clickRow={clickRow} setNuevaSolicitudes={setNuevaSolicitudes} />

      {/* Page header */}
      <div className="ds-page-head">
        <div>
          <div className="ds-page-title">
            Mis solicitudes
            <span className="count">{solicitudesGeneral.length} solicitudes · cruces sobre la traza</span>
          </div>
          <div className="ds-page-sub">Seguimiento de tus interferencias presentadas ante Oldelval.</div>
        </div>
        <button className="ds-btn ds-btn-primary" onClick={() => setOpenEdit(true)}>
          <PlusIcon /> Nueva solicitud
        </button>
      </div>

      {/* KPI row */}
      <div className="ds-kpi-row">
        {KPI_CONFIG.map(k => (
          <div className="ds-kpi-card" key={k.key}>
            <div className="ds-kpi-label">
              <span className="ds-kpi-dot" style={{ background: k.color }} />
              {k.label}
            </div>
            <div className="ds-kpi-value">{kpiValues[k.key]}</div>
          </div>
        ))}
      </div>

      {/* Banner de acciones pendientes */}
      {bannerTotal > 0 && (
        <div className="ds-banner">
          <div className="ds-banner-ic"><AlertIcon /></div>
          <div className="ds-banner-tx">
            <b>Tenés {bannerTotal} solicitud{bannerTotal > 1 ? 'es' : ''} que requieren tu acción</b>
            <p>
              {bannerItems.docuTecnica.length > 0 && <span>ID{bannerItems.docuTecnica.length > 1 ? 's' : ''} <span className="ds-banner-ids">{bannerItems.docuTecnica.join(', ')}</span> esperan documentación técnica. </span>}
              {bannerItems.docuLegal.length > 0 && <span>ID{bannerItems.docuLegal.length > 1 ? 's' : ''} <span className="ds-banner-ids">{bannerItems.docuLegal.join(', ')}</span> esperan documentación legal. </span>}
              {bannerItems.docuCAO.length > 0 && <span>ID{bannerItems.docuCAO.length > 1 ? 's' : ''} <span className="ds-banner-ids">{bannerItems.docuCAO.join(', ')}</span> esperan documentación CAO. </span>}
              {bannerItems.habilitadas.length > 0 && <span>ID{bannerItems.habilitadas.length > 1 ? 's' : ''} <span className="ds-banner-ids">{bannerItems.habilitadas.join(', ')}</span> habilitadas para inicio de obra.</span>}
            </p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="ds-toolbar">
        <div className="ds-search">
          <SearchIcon />
          <input
            className="ds-search-input"
            placeholder="Buscar por ID, obra, estado, tipo de instalación…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
        </div>
        <div className="ds-seg">
          {FILTER_OPTIONS.map(f => (
            <button
              key={f.key}
              className={stateFilter === f.key ? 'on' : ''}
              onClick={() => { setStateFilter(f.key); setPage(0); }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de solicitudes */}
      <div className="sol-list">
        {solicitudes && paginated.map(row => {
          const a = row.solicitud.attributes;
          const badge = estadoToBadge(a.estado);
          const n = normEst(a.estado);
          const isDraft = n === 'borrador';
          const isAttn = !isDraft && requiereAccion(a.estado);

          return (
            <div key={a.id} className={`sol-card${isAttn ? ' attn' : ''}${isDraft ? ' draft' : ''}`}>
              {/* Lead: nombre, meta, estado */}
              <div className="sw-lead">
                <div className="obra-name">{a.denominacion}</div>
                <div className="obra-meta">
                  <span className="obra-id">{String(a.id).padStart(5, '0')}</span>
                  {(a.detalle_instalacion || a.tipo_instalacion) && (
                    <><span className="sepdot" /><span>{a.detalle_instalacion || a.tipo_instalacion}</span></>
                  )}
                  {a.tipo_cruce && (
                    <><span className="sepdot" /><span>{a.tipo_cruce}</span></>
                  )}
                </div>
                <span className={`ds-badge ${badge.cls}`}>
                  <span className="dot" />
                  {badge.label}
                </span>
              </div>

              {/* Timeline: línea de vida */}
              {!isDraft && (
                <div className="sw-life">
                  <div className="sw-life-label">Línea de vida de la solicitud</div>
                  <div className="mini-steps">
                    {MINI_STEPS.map((lbl, i) => {
                      const { cls, sub } = getMiniStep(a, i);
                      return (
                        <div key={i} className={`ms${cls ? ' ' + cls : ''}`}>
                          <div className="node" />
                          <div className="lbl">{lbl}</div>
                          {sub && <div className="sub">{sub}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tail: acciones */}
              <div className="sw-tail">
                <ActionTail
                  row={row} setUpload={setUpload} setRow={setRow}
                  setOpenEdit={setOpenEdit} setEditRow={setEditRow}
                  setChangeState={setChangeState} loading={props.setLoading}
                  setNuevaSolicitudes={setNuevaSolicitudes}
                  setDeletedText={setDeletedText} setOpenDeleted={setOpenDeleted}
                />
              </div>
            </div>
          );
        })}

        {solicitudes && paginated.length === 0 && (
          <div className="sol-empty">
            {search || stateFilter !== 'all' ? 'Sin resultados para los filtros aplicados.' : 'No hay solicitudes registradas.'}
          </div>
        )}
      </div>

      {/* Paginación */}
      {filtered.length > rowsPerPage && (
        <div className="ds-pager">
          <span>Mostrando <b>{page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filtered.length)}</b> de {filtered.length}</span>
          <div className="ds-pages">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}><ChevLIcon /></button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} className={i === page ? 'on' : ''} onClick={() => setPage(i)}>{i + 1}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}><ChevRIcon /></button>
          </div>
        </div>
      )}

      <Snackbar open={openDeleted} autoHideDuration={6000} onClose={() => setOpenDeleted(false)}>
        <Alert severity="warning" sx={{ width: '100%' }}>{deletedText}</Alert>
      </Snackbar>

      {/* Sin autoHideDuration: si no se pudieron cargar las solicitudes, el
          aviso tiene que quedar hasta que el usuario lo cierre. */}
      <Snackbar open={!!loadError} onClose={() => setLoadError('')}>
        <Alert severity="error" onClose={() => setLoadError('')} sx={{ width: '100%' }}>
          {loadError}
        </Alert>
      </Snackbar>
    </>
  );
}
